import { Fragment, useState } from "react";
import { Box, Checkbox, FormControlLabel, Typography, useTheme } from "@mui/material";
import { Check } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useTextFieldColors } from "./useTextFieldColors";
import { AssignBoxType } from "../../FacilityManager/Components/Toolbars/InfoSidebar/AssignBox/AssignBox";
import { ButtonStyledText } from "../ButtonStyledText.partial";

type CompactTextFieldProps = {
  placeholder: string;
  value: string | number;
  newText?: string;
  rows?: number;
  onChange?: (newValue: string) => void;
  handleOk?: (newValue: string) => void;
  handleAssign?: () => void;
  setNewText?: (t: string) => void;
  readOnly?: boolean;
  buttonOkLabel?: string;
  buttonAssignLabel?: string;
  buttonDataTestId?: string;
  valueArr?: string[];
  type?: AssignBoxType;
  bottomChildren?: JSX.Element;
  isEditable?: boolean;
  isMandatoryForService?: boolean;
  setIsFieldEditing?: (isEditing: boolean) => void;
};

export function CompactTextField({
  placeholder,
  value,
  rows = 1,
  newText,
  setNewText,
  onChange,
  handleOk,
  handleAssign,
  readOnly = false,
  buttonOkLabel,
  buttonAssignLabel,
  buttonDataTestId,
  valueArr,
  type,
  bottomChildren,
  isEditable,
  isMandatoryForService,
  setIsFieldEditing
}: Readonly<CompactTextFieldProps>) {
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const { borderColor, placeholderBackgroundColor, textColor } = useTextFieldColors(isFocused);
  const { t } = useTranslation();
  const theme = useTheme();

  const noInvenAssgined = t("NoInventoryModalLabel");
  const noInvenName = t("NoInventoryAssigned");
  const isBottomBar = type === "Settings";
  const showCheckbox =
    type === "Bookable_Equipment" || type === "Catering" || type === "RoomLayout";
  const invenTextColor = newText === noInvenAssgined || newText === noInvenName ? "red" : textColor;

  function handleFocus() {
    setIsFocused(true);
    setIsFieldEditing?.(true);
  }

  function handleBlur() {
    setIsFocused(false);
    setIsFieldEditing?.(false);
  }

  return (
    <Box
      position={"relative"}
      flex={1}
      display={"flex"}
      alignItems={"center"}
      flexDirection={"column"}
      sx={{
        border: `1px solid ${borderColor}`,
        borderRadius: 0.5
      }}
    >
      {/* title */}
      <span
        style={{
          transitionDuration: "200ms",
          color: textColor,
          fontSize: 12,
          fontFamily: "'Fira Sans'",
          fontWeight: 600,
          borderTopLeftRadius: 4,
          borderTopRightRadius: 4,
          background: placeholderBackgroundColor,
          width: "100%",
          display: "flex",
          alignItems: "center",
          paddingTop: 4,
          paddingBottom: 4,
          paddingLeft: 4
        }}
      >
        {placeholder}
      </span>
      <div style={{ display: "inline-flex", alignSelf: "start", width: "100%" }}>
        {!isBottomBar && (
          <textarea
            readOnly={readOnly || !isEditable}
            value={newText}
            onChange={event => {
              onChange?.(event.target.value);
              setNewText?.(event.target.value);
            }}
            onFocus={handleFocus}
            onBlur={handleBlur}
            placeholder={placeholder}
            rows={rows}
            style={{
              color: invenTextColor,
              margin: 4,
              width: "100%",
              border: "none",
              background: "none",
              outline: "none",
              fontSize: 12,
              resize: "none",
              fontFamily: "'Fira Sans'",
              cursor: !readOnly && isEditable ? "text" : "default",
              alignContent: "center"
            }}
          />
        )}
        {/* bottom bar child */}
        {isBottomBar && (
          <Box
            component="fieldset"
            tabIndex={-1}
            sx={{
              width: "inherit",
              border: "none",
              margin: 0,
              padding: 0,
              minWidth: 0
            }}
            onFocus={handleFocus}
            onBlur={handleBlur}
          >
            {bottomChildren}
          </Box>
        )}

        {/* when equipment, display simply box and its list */}
        {valueArr && (
          <Box
            sx={{
              margin: 0.5,
              width: "100%",
              border: "none",
              background: "none",
              outline: "none",
              fontSize: 12,
              resize: "none",
              fontFamily: "'Fira Sans'",
              cursor: readOnly ? "default" : "text",
              whiteSpace: "pre-line",
              alignContent: "center",
              lineHeight: "20px"
            }}
            data-testid="equipment-list-box"
          >
            {valueArr?.length
              ? valueArr?.map(val => {
                  return (
                    <Fragment key={val}>
                      <Check sx={{ fontSize: 12, paddingTop: "3px" }} />
                      {`${val}\r\n`}
                    </Fragment>
                  );
                })
              : t("NoEquipmentLabel")}
          </Box>
        )}

        {handleOk && newText && isEditable && (
          <ButtonStyledText
            theme={theme}
            disabled={value === noInvenAssgined}
            data-testid={buttonDataTestId ?? "textfield-ok-btn"}
            onClick={() => handleOk(newText.toString())}
            sx={{ fontSize: 12 }}
          >
            {buttonOkLabel ?? t("Ok")}
          </ButtonStyledText>
        )}

        {handleAssign && isEditable && (
          <ButtonStyledText
            theme={theme}
            data-testid={buttonDataTestId ?? "textfield-assign-btn"}
            onClick={handleAssign}
            sx={{ fontSize: 12, minWidth: "fit-content" }}
          >
            {buttonAssignLabel ?? t("Assign")}
          </ButtonStyledText>
        )}
      </div>

      {showCheckbox && (
        <FormControlLabel
          disabled
          sx={{ width: "100%", mx: 0, mb: 0.25 }}
          control={
            isMandatoryForService ? (
              <Check sx={{ px: 0.5, py: 0.5 }} />
            ) : (
              <Checkbox checked={false} size="small" sx={{ px: 0.5, py: 0.5 }} />
            )
          }
          label={
            <Typography sx={{ fontSize: 12, pt: isMandatoryForService ? 0.125 : 0 }}>
              {t("Mandatory")}
            </Typography>
          }
        />
      )}
    </Box>
  );
}
